// HomeScreen.jsx
import React, { useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import { useAuth } from "../Context/AuthContext";

const steps = [
  "RFP summarization",
  "RFP completeness",
  "Carrier translation",
  "Quote information consolidation",
];

const HomeScreen = ({ ToEvaluation }) => {

  const { sessionId } = useAuth();

  useEffect(() => {
    //Just printing it here
    console.log(sessionId);
  }, [sessionId])
  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        py: 3,
        px: 2,
        display: "flex",
        justifyContent: "center",
      }}
    >
      {/* Outer container – 95vw */}
      <Box sx={{ width: "95vw", maxWidth: "1500px" }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1.6rem", md: "2rem" },
              fontWeight: 700,
            }}
          >
            Hi, Kelly! Here are the opportunities assigned to you.
          </Typography>

          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              placeholder="Search by Client Name or Salesforce ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {/* ---------- 3-Column CARD LAYOUT (NO GRID) ---------- */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            justifyContent: "flex-start",
          }}
        >
          {/* Card 1 */}
          <CardBox
            company="Christmas Township"
            sfid="SF-WMT-2024-001"
            product="Group Dental"
            status="RFP Summarization Complete"
            progressStatus={[1]}
            ToEvaluation={ToEvaluation}
          />

          {/* Card 2 */}
          <CardBox
            company="Company E"
            sfid="SF-AMZ-2024-015"
            product="Group Life"
            status="Completeness Check"
            progressStatus={[1, 2]}
            ToEvaluation={ToEvaluation}
          />

          {/* Card 3 */}
          {/* <CardBox
            company="Microsoft Corporation"
            sfid="SF-MSFT-2024-008"
            product="Group Medical"
            status="Quote Consolidation Complete"
            progressStatus={[1, 2, 3, 4]}
            ToEvaluation={ToEvaluation}
          /> */}
        </Box>
      </Box>
    </Box>
  );
};

export default HomeScreen;

/* --------------------------------------------------------- */
/* ---------------------- CARD COMPONENT -------------------- */
/* --------------------------------------------------------- */

const CardBox = ({
  company,
  sfid,
  product,
  status,
  progressStatus,
  ToEvaluation,
}) => {
  return (
    <Card
      sx={{
        width: { xs: "100%", md: "31%" }, // 3 cards per row
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {company}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {sfid}
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
          {product}
        </Typography>

        <Chip
          label={status}
          size="small"
          sx={{
            mt: 2,
            fontWeight: 500,
            borderRadius: 10,
            color: "primary.main",
            borderColor: "primary.main",
            bgcolor: "rgba(5,102,192,0.08)",
          }}
          variant="outlined"
        />

        {/* Progress */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500, mb: 1.5 }}
          >
            Progress
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            {steps.map((label, idx) => (
              <React.Fragment key={idx}>
                <StepCircle
                  index={idx + 1}
                  label={label}
                  completed={progressStatus.includes(idx + 1)}
                  active={
                    progressStatus.includes(idx + 1) &&
                    progressStatus[progressStatus.length - 1] === idx + 1
                  }
                />

                {/* Add connector except for last item */}
                {idx !== steps.length - 1 && (
                  <Box
                    sx={{
                      flex: 1,
                      height: 1,
                      mx: 1,
                      bgcolor: progressStatus.includes(idx + 1)
                        ? "primary.main"
                        : "divider",
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </CardContent>

      <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          sx={{ py: 1.1, borderRadius: 2 }}
          onClick={() => {
            ToEvaluation(company, product);
          }}
        >
          Review Opportunity
        </Button>
      </CardActions>
    </Card>
  );
};

/* --------------------------------------------------------- */
/* ---------------------- STEP CIRCLE ----------------------- */
/* --------------------------------------------------------- */

const StepCircle = ({ index, label, completed, active }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      minWidth: 64,
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        bgcolor: completed ? "primary.main" : "divider",
        color: completed ? "#fff" : "text.secondary",
      }}
    >
      {completed && index !== 4 ? <CheckIcon sx={{ fontSize: 16 }} /> : index}
    </Box>

    <Typography
      variant="caption"
      sx={{
        mt: 1,
        textAlign: "center",
        fontWeight: active ? 600 : 400,
        color: completed ? "text.primary" : "text.secondary",
      }}
    >
      {label}
    </Typography>
  </Box>
);
